import React from 'react';
import RadioMusicVisual from './RadioMusicVisual';
import RadioQualifiedListenInfo from './RadioQualifiedListenInfo';
import RadioListenerPresenceRow, {
  getRadioListenerPresenceCount,
} from './RadioListenerPresenceRow';
import { RADIO_QUEUE_KINDS } from './hitLabRadioTracks';
import { buildRadioLiveCapsuleMessage } from './radioLiveCapsuleMessages';

const formatQualifiedListens = (value) => {
  const count = Number(value);
  if (!Number.isFinite(count) || count < 0) return '';
  return `${Math.floor(count).toLocaleString()} plays`;
};

const formatTime = (value) => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
};

export default function RadioRecordCapsuleCard({
  capsule,
  isPlaying = false,
  isLoading = false,
  elapsed = 0,
  duration = 0,
  signals = null,
  liveSignals = null,
  activeQueue = [],
  currentIndex = 0,
  transientFeedbackSignal = null,
  transientNewInRotationSignal = null,
}) {
  if (!capsule) return null;

  const progress =
    duration > 0 ? Math.min(100, Math.max(0, (elapsed / duration) * 100)) : 0;
  const qualifiedListens = formatQualifiedListens(
    capsule.qualifiedListenCount,
  );
  const showQualifiedListens =
    capsule.kind === RADIO_QUEUE_KINDS.TRACK && Boolean(qualifiedListens);
  const coverAlt = capsule.coverArtUrl
    ? `Cover art for ${capsule.title} by ${capsule.artistName}`
    : '';
  const linkedArtistPresence = liveSignals?.linkedArtistPresence || {};
  const artistPresenceClass = linkedArtistPresence.isInRoom
    ? 'is-in-room'
    : linkedArtistPresence.isOnline
      ? 'is-online'
      : '';
  const artistPresenceLabel = linkedArtistPresence.isInRoom
    ? 'In JG Radio now'
    : linkedArtistPresence.isOnline
      ? 'Online in JG Universe'
      : '';
  const stationListenerPresence = liveSignals?.stationListenerPresence || null;
  const listenerPresence = liveSignals?.listenerPresence || null;
  const activeListenerCount =
    getRadioListenerPresenceCount(stationListenerPresence) +
    getRadioListenerPresenceCount(listenerPresence);
  const dockMessage = buildRadioLiveCapsuleMessage({
    currentCapsule: capsule,
    queue: activeQueue,
    currentIndex,
    isPlaying,
    signals,
    transientFeedbackSignal,
    transientNewInRotationSignal,
  });
  const showDockMessage =
    !liveSignals?.movementText &&
    activeListenerCount <= 0 &&
    Boolean(dockMessage.secondaryLabel) &&
    (dockMessage.secondaryLabel !== 'In Rotation' ||
      Boolean(dockMessage.secondaryTitle));

  return (
    <article
      className="jg-radio-dock-capsule"
      aria-label={`${capsule.title} by ${capsule.artistName}`}
    >
      <div className="jg-radio-dock-art">
        {capsule.coverArtUrl ? (
          <img
            src={capsule.coverArtUrl}
            alt={coverAlt}
            width="76"
            height="76"
            decoding="async"
          />
        ) : (
          <div
            className="jg-radio-dock-art-fallback"
            aria-label="Joe Galaxy Radio artwork"
          >
            <span>JG</span>
          </div>
        )}
      </div>

      <div className="jg-radio-dock-record">
        <div className="jg-radio-dock-state-row">
          <span className="jg-radio-dock-state">
            {capsule.kind === 'bumper' ? 'Station Sweep' : 'Now Playing'}
          </span>
          {capsule.isInRotation ? (
            <span className="jg-radio-dock-badge">In Rotation</span>
          ) : null}
        </div>
        <h2>{capsule.title}</h2>
        <p className="jg-radio-dock-artist">
          {artistPresenceClass ? (
            <span
              className={[
                'jg-radio-live-capsule-presence-dot',
                artistPresenceClass,
              ]
                .filter(Boolean)
                .join(' ')}
              aria-label={artistPresenceLabel}
              title={artistPresenceLabel}
              role="img"
            />
          ) : null}
          <span>{capsule.artistName}</span>
        </p>
        {showQualifiedListens || capsule.isNewInRotation ? (
          <span className="jg-radio-dock-count-row">
            {showQualifiedListens ? (
              <span className="jg-radio-dock-count-wrap">
                <span className="jg-radio-dock-count">{qualifiedListens}</span>
                <RadioQualifiedListenInfo />
              </span>
            ) : null}
            {capsule.isNewInRotation ? (
              <span
                className="jg-radio-dock-new-badge"
                title="Just added. Early spins are normal while this record starts its run."
              >
                NEW IN ROTATION
              </span>
            ) : null}
          </span>
        ) : null}
        {liveSignals?.movementText ? (
          <span className="jg-radio-dock-live-movement" role="status">
            {liveSignals.movementText}
          </span>
        ) : null}
        {showDockMessage ? (
          <span className="jg-radio-dock-capsule-signal" role="status">
            <span>{dockMessage.secondaryLabel}</span>
            {dockMessage.secondaryTitle ? (
              <strong>
                {dockMessage.secondaryTitle}
                {dockMessage.secondaryArtistName
                  ? ` - ${dockMessage.secondaryArtistName}`
                  : ''}
              </strong>
            ) : null}
          </span>
        ) : null}
        <RadioListenerPresenceRow
          presence={stationListenerPresence}
          label="listening across JG Radio"
          className="jg-radio-dock-listener-row is-station"
          avatarClassName="jg-radio-dock-listener"
        />
        <RadioListenerPresenceRow
          presence={listenerPresence}
          label="listening to this record"
          className="jg-radio-dock-listener-row"
          avatarClassName="jg-radio-dock-listener"
        />
        <RadioMusicVisual
          className="jg-radio-dock-bars"
          isPlaying={isPlaying}
          barCount={4}
        />
      </div>

      <div className="jg-radio-dock-progress-row">
        <span>{formatTime(elapsed)}</span>
        <div
          className="jg-radio-dock-progress"
          role="progressbar"
          aria-label="Radio dock playback progress"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={Math.round(progress)}
        >
          <span style={{ width: `${progress}%` }} />
        </div>
        <span>{isLoading ? '...' : formatTime(duration)}</span>
      </div>
    </article>
  );
}
